import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useAuth, useFirestore, useUserCompany } from '../hooks/useFirebase';

export const name = 'getCostLimit';
export const description = 'Retrieves the cost budget (költség keret) statistics for a project number directly from the database.';
export const parameters = {
  projectNumber: 'Optional project number (e.g., "M-22-04") to query. If not provided, it falls back to the currently selected project or the user\'s first available project.'
};
export const outputTextForUI = 'Költségkeret lekérése';
export const useOnMobile = true;

export async function getCostLimit(args?: { projectNumber?: string }) {
  const db = useFirestore();
  const company = await useUserCompany();
  const user = useAuth();

  let selectedProj = args?.projectNumber;

  // Fallback if no project number is provided
  if (!selectedProj) {
    const userRef = doc(db, 'user', user.uid);
    const userSnap = await getDoc(userRef);
    const projectNumbers = userSnap.data()?.project_numbers || [];
    if (projectNumbers.length > 0) {
      selectedProj = projectNumbers[0];
    }
  }

  if (!selectedProj) {
    throw new Error('Nem található munkaszám. Kérlek adj meg egy érvényes munkaszámot.');
  }

  // 1. Fetch data in parallel from Firestore for calculations
  const [dailySnapshot, planSnapshot, subSnapshot] = await Promise.all([
    getDocs(query(collection(db, 'daily'), where('company', '==', company), where('project_number', '==', selectedProj))),
    getDocs(query(collection(db, 'plan'), where('company', '==', company), where('project_number', '==', selectedProj))),
    getDocs(query(collection(db, 'contract'), where('customer', '==', company), where('project_number', '==', selectedProj)))
  ]);

  // 2. Perform CostBudget calculations
  let dailyCostWithoutContracts = 0;
  dailySnapshot.forEach((doc) => {
    const data = doc.data();
    if (data && typeof data.final_price === 'number') {
      dailyCostWithoutContracts += data.final_price;
    }
  });

  let plannedCostWithoutContracts = 0;
  planSnapshot.forEach((doc) => {
    const data = doc.data();
    if (data && typeof data.final_price === 'number') {
      plannedCostWithoutContracts += data.final_price;
    }
  });

  let subcontractorContractsReadiness = 0;
  let subcontractorContractsPrice = 0;
  subSnapshot.forEach((doc) => {
    const data = doc.data();
    if (data) {
      if (typeof data.forint_readiness === 'number') {
        subcontractorContractsReadiness += data.forint_readiness;
      }
      if (typeof data.contract_price === 'number') {
        subcontractorContractsPrice += data.contract_price;
      }
      if (typeof data.after_work === 'number') {
        subcontractorContractsPrice += data.after_work;
      }
    }
  });

  const realCost = dailyCostWithoutContracts + subcontractorContractsReadiness;
  const plannedCost = plannedCostWithoutContracts + subcontractorContractsPrice;
  const percentageBar = plannedCost > 0 ? Number(((realCost / plannedCost) * 100).toFixed(2)) : 0;

  return {
    success: true,
    projectNumber: selectedProj,
    metrics: {
      dailyCostWithoutContracts,
      subcontractorContractsReadiness,
      realCost,
      plannedCostWithoutContracts,
      subcontractorContractsPrice,
      plannedCost,
      percentageBar
    }
  };
}
